import { PropsWithChildren } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Heading, LinkBox, LinkOverlay, List, ListItem, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { getSchoolShortName } from '~/features/program/utils/get-school-data';

import { ArrowRightIcon, SchoolIcon } from '../icons';

import { SectionHeader } from './section-header';

const COLOR_MAP = {
  blue: 'blue.500',
  purple: 'purple.500',
  cyan: 'cyan.700',
  orange: 'orange.700',
} as const;

const SCHOOLS = (
  [
    {
      slug: 'artificial-intelligence',
      color: 'blue',
      iconPath: '/images/school-icons/shields/artificial-intelligence.svg',
    },
    {
      slug: 'data-science',
      color: 'blue',
      iconPath: '/images/school-icons/shields/data-science.svg',
    },
    {
      slug: 'business',
      color: 'purple',
      iconPath: '/images/school-icons/shields/business.svg',
    },
    {
      slug: 'executive-leadership',
      color: 'purple',
      iconPath: '/images/school-icons/shields/executive-leadership.svg',
    },
    {
      slug: 'programming',
      color: 'cyan',
      iconPath: '/images/school-icons/shields/programming.svg',
    },
    {
      slug: 'cloud-computing',
      color: 'cyan',
      iconPath: '/images/school-icons/shields/cloud-computing.svg',
    },
    {
      slug: 'autonomous-systems',
      color: 'orange',
      iconPath: '/images/school-icons/shields/autonomous-systems.svg',
    },
    {
      slug: 'cybersecurity',
      color: 'orange',
      iconPath: '/images/school-icons/shields/cybersecurity.svg',
    },
    {
      slug: 'product-management',
      color: 'purple',
      iconPath: '/images/school-icons/shields/product-management.svg',
    },
    {
      slug: 'career-resources',
      color: 'purple',
      iconPath: '/images/school-icons/shields/career-resources.svg',
    },
    {
      slug: 'animation-and-game-development',
      color: 'orange',
      iconPath: '/images/school-icons/shields/animation-and-game-dev.svg',
    },
    {
      slug: 'devops',
      color: 'cyan',
      iconPath: '/images/school-icons/shields/devops.svg',
    },
  ] as const
).map((school) => ({
  ...school,
  color: COLOR_MAP[school.color as keyof typeof COLOR_MAP],
}));

export type UdacitySchoolsSectionProps = PropsWithChildren<{}>;

export function UdacitySchoolsSection({ children }: UdacitySchoolsSectionProps) {
  const { t, i18n } = useTranslation();
  return (
    <Box
      as='section'
      bgColor='grey.50'
      padding={{
        base: '56px 0px',
        md: '120px 0px',
      }}
    >
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Flex
          direction='column'
          gap={{
            base: '40px',
            md: '72px',
          }}
        >
          <SectionHeader
            color='grey.500'
            descriptionSlot={<Text color='grey.500'>{t('home.schools.description')}</Text>}
            iconSlot={<SchoolIcon color='grey.500' height='15px' width='15px' />}
            subtitle={t('home.schools.subtitle') ?? ''}
            titleSlot={
              <Heading
                as='h2'
                fontWeight='600'
                lineHeight='1.1'
                fontSize={{
                  base: '2.25rem',
                  md: '3.375rem',
                }}
              >
                {t('home.schools.title')}
              </Heading>
            }
          />
          <List
            display='grid'
            gap={{
              base: 2,
              md: 6,
            }}
            gridTemplateColumns={{
              md: 'repeat(2, 1fr)',
            }}
          >
            {SCHOOLS.map((school) => {
              const defaultName = getSchoolShortName(school.slug, t, 'en-US');
              const localizedName = getSchoolShortName(school.slug, t, i18n.language);

              return (
                <ListItem key={school.slug}>
                  <LinkBox
                    alignItems='center'
                    bgColor='white'
                    borderRadius='8px'
                    display='flex'
                    gap={6}
                    padding={{
                      base: '14px 24px',
                      md: '24px 32px',
                    }}
                  >
                    <Box
                      display={{
                        base: 'none',
                        md: 'block',
                      }}
                    >
                      <Image
                        alt=''
                        height={64}
                        src={school.iconPath}
                        width={56}
                        style={{
                          pointerEvents: 'none',
                        }}
                      />
                    </Box>
                    <Box
                      display={{
                        base: 'block',
                        md: 'none',
                      }}
                    >
                      <Image alt='' height={42} src={school.iconPath} style={{ pointerEvents: 'none' }} width={36} />
                    </Box>
                    <LinkOverlay
                      color='grey.900'
                      display='flex'
                      flex={1}
                      flexDirection='column'
                      fontFamily='heading'
                      fontWeight='600'
                      gap='6px'
                      href={`/school/${school.slug}`}
                      fontSize={{
                        base: '1.125rem',
                        md: '1.5rem',
                      }}
                      lineHeight={{
                        base: '1.2',
                        md: '1.14',
                      }}
                      onClick={() =>
                        trackEvent({
                          name: 'Homepage CTA Clicked',
                          type: 'click',
                          label: 'school_card_clicked',
                          value: defaultName,
                        })
                      }
                    >
                      <Box
                        as='span'
                        color={school.color}
                        fontFamily='condensed'
                        fontSize={{ base: '0.8125rem', md: '0.9375rem' }}
                        fontWeight='500'
                        lineHeight='1'
                        textTransform='uppercase'
                      >
                        {t('common.schoolOf')}
                      </Box>
                      {localizedName}
                    </LinkOverlay>
                    <ArrowRightIcon
                      color='grey.300'
                      height={{ md: '32px', base: '24px' }}
                      opacity={0.5}
                      pointerEvents='none'
                      width={{ md: '32px', base: '24px' }}
                    />
                  </LinkBox>
                </ListItem>
              );
            })}
          </List>
          {children}
        </Flex>
      </ContentContainer>
    </Box>
  );
}
